import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Eye, Trash2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtTime } from "@/lib/api";
import { card, Empty, inputCls, Sel } from "@/components/admin/kit";
import { StatusBadge, useGet, waitText } from "./common";

export function SendBackDialog({ api, item, onClose, onDone }) {
  const [f, setF] = useState({ reason: "Blurry", note: "" });
  const go = async () => {
    try { await api.post(`/subs/${item.id}/send-back`, f); toast.success(`${item.case_no} sent back`); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="send-back-dialog">
        <DialogHeader><DialogTitle>Send back {item.case_no}</DialogTitle></DialogHeader>
        <Sel value={f.reason} onChange={(v) => setF({ ...f, reason: v })} options={["Blurry", "Missing timing", "Wrong patient", "Other"].map((r) => [r, r])} testid="send-back-reason" />
        <textarea className={`${inputCls} min-h-[80px]`} placeholder="Note for the front desk (needed for Other)" value={f.note} maxLength={300} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="send-back-note" />
        <Button onClick={go} variant="destructive" data-testid="send-back-confirm">Send back</Button>
      </DialogContent>
    </Dialog>
  );
}

const OPEN = "received,ai_reading,ready";

function Filters({ f, setF, orgs }) {
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <div className="mb-4 grid gap-2 sm:grid-cols-5" data-testid="queue-filters">
      <Sel value={f.org_id} onChange={set("org_id")} placeholder="All organisations" options={[["", "All organisations"], ...(orgs || []).map((o) => [o.id, o.name])]} testid="queue-filter-org" />
      <Sel value={f.status} onChange={set("status")} options={[[OPEN, "Waiting to check"], ["", "All statuses"], ["approved", "Approved"], ["sent_back", "Sent back"], ["duplicate", "Duplicate"]]} testid="queue-filter-status" />
      <input type="date" className={inputCls} value={f.date_from} onChange={(e) => set("date_from")(e.target.value)} data-testid="queue-filter-from" />
      <input type="date" className={inputCls} value={f.date_to} onChange={(e) => set("date_to")(e.target.value)} data-testid="queue-filter-to" />
      <input className={inputCls} placeholder="Case, name or phone" value={f.q} onChange={(e) => set("q")(e.target.value)} data-testid="queue-search" />
    </div>
  );
}

function Row({ s, base, api, me, reload, onSendBack }) {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const open = ["received", "ai_reading", "ready"].includes(s.status);
  return (
    <tr className={`border-b border-[var(--lav-2)] align-top ${s.late ? "bg-orange-50" : ""}`} data-testid={`queue-row-${s.case_no}`}>
      <td className="py-2 pr-2 font-bold">{s.case_no}{s.letterhead_flag && <span className="ml-1 rounded bg-red-600 px-1 text-[10px] text-white" title={s.letterhead_flag}>letterhead</span>}</td>
      <td className="pr-2">{s.org}<p className="text-xs text-muted-foreground">{s.doctor || "—"}</p></td>
      <td className="pr-2">{s.patient}<p className="text-xs text-muted-foreground">{s.phone}</p></td>
      <td className="pr-2">{s.ai_status === "reading" || s.ai_status === "waiting" ? "reading…" : s.ai_status === "failed" ? <span className="font-bold text-red-700" data-testid="queue-ai-failed">AI read failed</span> : `${s.medicines} found`}{s.has_appointment ? " · Appointment" : ""}</td>
      <td className="pr-2 text-xs">{fmtTime(s.created_at)}<p className={s.late ? "font-bold text-orange-700" : "text-muted-foreground"} data-testid="queue-waiting">{open ? `waiting ${waitText(s.waiting_min)}` : ""}</p></td>
      <td className="pr-2"><StatusBadge s={s.status} testid="queue-status" /></td>
      <td className="whitespace-nowrap">
        <Link to={`${base}/case/${s.id}`} className="inline-flex h-8 items-center gap-1 rounded-lg border px-2 text-xs font-semibold hover:bg-[var(--lav)]" data-testid={`queue-view-${s.case_no}`}><Eye size={14} /> View</Link>
        {open && <Button size="sm" variant="ghost" onClick={() => onSendBack(s)} data-testid={`queue-sendback-${s.case_no}`}><Undo2 size={14} /></Button>}
        {open && <Button size="sm" variant="ghost" title="Mark duplicate" onClick={() => { const ref = window.prompt(`${s.case_no} is a duplicate of which case? Leave empty to use the latest case with the same phone.`, ""); if (ref !== null) act(() => api.post(`/subs/${s.id}/duplicate`, { duplicate: true, dup_of: ref }), "Marked duplicate"); }} data-testid={`queue-dup-${s.case_no}`}><Copy size={14} /></Button>}
        {me?.role === "super_admin" && s.status !== "approved" && <Button size="sm" variant="ghost" title="Delete" onClick={() => window.confirm(`Delete ${s.case_no}? This cannot be undone.`) && act(() => api.delete(`/subs/${s.id}`), "Deleted")} data-testid={`queue-delete-${s.case_no}`}><Trash2 size={14} /></Button>}
      </td>
    </tr>
  );
}

export function Queue({ api, base }) {
  const [f, setF] = useState({ org_id: "", status: OPEN, q: "", date_from: "", date_to: "" });
  const [sb, setSb] = useState(null);
  const qs = new URLSearchParams(f).toString();
  const [d, err, reload] = useGet(api, `/subs?${qs}`);
  return (
    <div data-testid="submissions-queue">
      <div className="mb-4"><p className="text-2xl font-bold">Submissions</p><p className="text-sm text-muted-foreground">Oldest first. Orange rows have waited more than 2 hours. Approve only inside the case view.</p></div>
      <Filters f={f} setF={setF} orgs={d?.orgs} />
      <div className={`${card} overflow-x-auto`}>
        {err ? <p className="text-red-700" data-testid="queue-error">{err}</p> : !d ? <Empty>Loading…</Empty> : !d.items.length ? <Empty>No cases.</Empty> : (
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th>Case</th><th>Organisation · doctor</th><th>Patient</th><th>Medicines</th><th>Submitted</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>{d.items.map((s) => <Row key={s.id} s={s} base={base} api={api} me={d.me} reload={reload} onSendBack={setSb} />)}</tbody>
          </table>
        )}
      </div>
      {sb && <SendBackDialog api={api} item={sb} onClose={() => setSb(null)} onDone={reload} />}
    </div>
  );
}
